import React, { useEffect, useImperativeHandle, useRef } from 'react';
import { getTurnstileSiteKey, isTurnstileEnabled } from '../../utils/turnstile';
import './Turnstile.css';

const SCRIPT_ID = 'cf-turnstile-script';
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

// Script birden fazla formda kullanılsa da sayfaya bir kez yüklenir.
let scriptPromise = null;

const loadTurnstileScript = () => {
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      existing.addEventListener('load', resolve);
      existing.addEventListener('error', reject);
      return;
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = resolve;
    script.onerror = () => {
      // Başarısız denemeyi hatırlamıyoruz ki sonraki mount tekrar denesin.
      scriptPromise = null;
      reject(new Error('Turnstile script could not be loaded.'));
    };

    document.head.appendChild(script);
  });

  return scriptPromise;
};

// Cloudflare Turnstile widget'ı.
//
// onVerify(token) doğrulama tamamlandığında çağrılır. Token TEK KULLANIMLIK ve
// yaklaşık 5 dakika sonra geçersiz olur; bu yüzden süre dolduğunda ya da hata
// oluştuğunda onVerify('') ile üst forma "token'ı sil" denir.
//
// ref üzerinden reset() sunuluyor: gönderim reddedildikten sonra üst form
// widget'ı sıfırlar, aksi halde kullanıcı sayfayı yenilemeden tekrar
// deneyemez (aynı token ikinci kez kabul edilmez).
const Turnstile = React.forwardRef(({ action, onVerify, onError }, ref) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);

  // Callback'ler her render'da yeniden üretilebilir; effect'in bu yüzden
  // widget'ı söküp yeniden basmasını istemiyoruz.
  const onVerifyRef = useRef(onVerify);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onVerifyRef.current = onVerify;
    onErrorRef.current = onError;
  }, [onVerify, onError]);

  useImperativeHandle(
    ref,
    () => ({
      reset: () => {
        if (window.turnstile && widgetIdRef.current !== null) {
          window.turnstile.reset(widgetIdRef.current);
        }
      },
    }),
    []
  );

  useEffect(() => {
    if (!isTurnstileEnabled()) return undefined;

    let cancelled = false;

    loadTurnstileScript()
      .then(() => {
        // StrictMode geliştirme modunda effect'i iki kez çalıştırır; ikinci
        // mount'tan önce sökülen widget'ı tekrar basmamak için kontrol.
        if (cancelled || !containerRef.current || widgetIdRef.current !== null) return;

        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: getTurnstileSiteKey(),
          action,
          callback: (token) => onVerifyRef.current && onVerifyRef.current(token),
          'expired-callback': () => onVerifyRef.current && onVerifyRef.current(''),
          'timeout-callback': () => onVerifyRef.current && onVerifyRef.current(''),
          'error-callback': () => {
            if (onVerifyRef.current) onVerifyRef.current('');
            if (onErrorRef.current) onErrorRef.current();
          },
        });
      })
      .catch(() => {
        if (cancelled) return;
        if (onErrorRef.current) onErrorRef.current();
      });

    return () => {
      cancelled = true;
      if (window.turnstile && widgetIdRef.current !== null) {
        window.turnstile.remove(widgetIdRef.current);
      }
      widgetIdRef.current = null;
    };
  }, [action]);

  if (!isTurnstileEnabled()) return null;

  return <div className="turnstile-widget" ref={containerRef} />;
});

Turnstile.displayName = 'Turnstile';

export default Turnstile;
